import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Box, Group, InputLabel, Text, Tooltip, ActionIcon } from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconList,
  IconListNumbers,
  IconHeading,
  IconLink,
  IconPhoto,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconMinus,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './ProductRichTextEditor.module.css';

export interface ProductRichTextEditorProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  error?: string | null;
}

export function ProductRichTextEditor({
  label,
  value,
  onChange,
  disabled = false,
  error,
}: ProductRichTextEditorProps) {
  const { t } = useTranslation();

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
          link: {
            openOnClick: false,
            HTMLAttributes: {
              rel: 'noopener noreferrer',
              target: '_blank',
            },
          },
        }),
        Image.configure({
          HTMLAttributes: {
            style: 'max-width:100%;height:auto;display:block;',
          },
        }),
        Placeholder.configure({
          placeholder: t('editProduct.descriptionPlaceholder'),
        }),
      ],
      content: value || '',
      editable: !disabled,
      immediatelyRender: false,
      shouldRerenderOnTransaction: true,
      onUpdate: ({ editor: ed }) => {
        onChange(ed.getHTML());
      },
    },
    [disabled]
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const next = value || '';
    const current = editor.getHTML();
    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const promptUrl = (messageKey: string): string | null => {
    const raw = window.prompt(t(messageKey));
    if (raw === null) return null;
    const trimmed = raw.trim();
    return trimmed || null;
  };

  return (
    <Box mb="md">
      {label ? (
        <InputLabel size="sm" fw={500} mb={6} display="block">
          {label}
        </InputLabel>
      ) : null}

      <div className={classes.wrap}>
        <div className={classes.toolbar}>
          <Tooltip label={t('editProduct.richText.bold')}>
            <ActionIcon
              variant={editor?.isActive('bold') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              aria-label={t('editProduct.richText.bold')}
            >
              <IconBold size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('editProduct.richText.italic')}>
            <ActionIcon
              variant={editor?.isActive('italic') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              aria-label={t('editProduct.richText.italic')}
            >
              <IconItalic size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('editProduct.richText.underline')}>
            <ActionIcon
              variant={editor?.isActive('underline') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              aria-label={t('editProduct.richText.underline')}
            >
              <IconUnderline size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label={t('editProduct.richText.heading')}>
            <ActionIcon
              variant={editor?.isActive('heading', { level: 2 }) ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 2 }).run()
              }
              aria-label={t('editProduct.richText.heading')}
            >
              <IconHeading size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label={t('editProduct.richText.bulletList')}>
            <ActionIcon
              variant={editor?.isActive('bulletList') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              aria-label={t('editProduct.richText.bulletList')}
            >
              <IconList size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('editProduct.richText.orderedList')}>
            <ActionIcon
              variant={editor?.isActive('orderedList') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              aria-label={t('editProduct.richText.orderedList')}
            >
              <IconListNumbers size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label={t('editProduct.richText.horizontalRule')}>
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={!editor || disabled}
              onClick={() => editor?.chain().focus().setHorizontalRule().run()}
              aria-label={t('editProduct.richText.horizontalRule')}
            >
              <IconMinus size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label={t('editProduct.richText.link')}>
            <ActionIcon
              variant={editor?.isActive('link') ? 'filled' : 'subtle'}
              size="sm"
              disabled={!editor || disabled}
              onClick={() => {
                if (!editor) return;
                const url = promptUrl('editProduct.richText.promptLinkUrl');
                if (url === null) return;
                if (url === '') {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  return;
                }
                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              }}
              aria-label={t('editProduct.richText.link')}
            >
              <IconLink size={18} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label={t('editProduct.richText.image')}>
            <ActionIcon
              variant="subtle"
              size="sm"
              disabled={!editor || disabled}
              onClick={() => {
                if (!editor) return;
                const src = promptUrl('editProduct.richText.promptImageUrl');
                if (!src) return;
                const alt =
                  window.prompt(t('editProduct.richText.promptImageAlt'), '')?.trim() || '';
                editor.chain().focus().setImage({ src, alt }).run();
              }}
              aria-label={t('editProduct.richText.image')}
            >
              <IconPhoto size={18} />
            </ActionIcon>
          </Tooltip>

          <Group gap={4} ml="auto">
            <Tooltip label={t('editProduct.richText.undo')}>
              <ActionIcon
                variant="subtle"
                size="sm"
                disabled={!editor || disabled || !editor.can().undo()}
                onClick={() => editor?.chain().focus().undo().run()}
                aria-label={t('editProduct.richText.undo')}
              >
                <IconArrowBackUp size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('editProduct.richText.redo')}>
              <ActionIcon
                variant="subtle"
                size="sm"
                disabled={!editor || disabled || !editor.can().redo()}
                onClick={() => editor?.chain().focus().redo().run()}
                aria-label={t('editProduct.richText.redo')}
              >
                <IconArrowForwardUp size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </div>

        <EditorContent editor={editor} className={classes.editor} />
      </div>

      {error ? (
        <Text size="xs" c="red" mt={6}>
          {error}
        </Text>
      ) : (
        <Text size="xs" c="dimmed" mt={6}>
          {t('editProduct.descriptionHint')}
        </Text>
      )}
    </Box>
  );
}
